export const todayIso = () => new Date().toISOString().slice(0, 10);

export const supplierLabel = (supplier) => supplier?.companyName || supplier?.legalName || '';

export const productLabel = (product) => {
  if (!product) return '';
  const code = product.code ? `${product.code} - ` : '';
  const format = product.prformat?.name ? ` (${product.prformat.name})` : '';
  return `${code}${product.description || ''}${format}`.trim();
};

export const getOrderCode = (order) => order?.public_code || order?.supplier_code || `OC #${order?.id || ''}`;

export const formatDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

export const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const orderQuantityTotal = (order) =>
  (order?.order_details || []).reduce((sum, detail) => sum + numberValue(detail.pending_quantity ?? detail.quantity), 0);

export const normalizeOrderDetail = (order, detail) => {
  const product = detail?.product || {};
  return {
    key: `${order?.id || 'order'}-${detail?.order_detail_id || detail?.id}`,
    order_id: order?.id,
    order_code: getOrderCode(order),
    order_date: detail?.order_date || order?.order_date || order?.createdAt || order?.dateTime,
    order_detail_id: detail?.order_detail_id || detail?.id,
    product_id: detail?.product_id || product?.id,
    product,
    product_code: detail?.product_code || product?.code || '',
    product_description: product?.description || '',
    format_name: detail?.format_name || detail?.inferred_format_name || product?.prformat?.name || '',
    quantity_base: numberValue(detail?.pending_quantity ?? detail?.remaining_quantity ?? detail?.quantity),
    quantity_order: numberValue(detail?.quantity),
  };
};

export const baseProductRowsFromOrders = (orders) =>
  (orders || []).flatMap((order) =>
    (order?.order_details || [])
      .map((detail) => normalizeOrderDetail(order, detail))
      .filter((row) => row.order_id && row.order_detail_id && row.product_id)
  );

export const variantModeOptions = [
  { value: 'exact', label: 'Exacta' },
  { value: 'unknown_variant', label: 'Variante desconocida' },
  { value: 'unique_product', label: 'Producto unico' },
  { value: 'pending', label: 'Pendiente' },
];

export const variantModeLabel = (value) =>
  variantModeOptions.find((option) => option.value === value)?.label || value || '';

export const documentTypeOptions = [
  { value: 'promo_invoice', label: 'Factura promocional' },
  { value: 'credit_note', label: 'Nota de credito' },
];

export const documentTypeLabel = (value) =>
  documentTypeOptions.find((option) => option.value === value)?.label || value || '';

export const fiscalStatusOptions = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'partially_applied', label: 'Parcial' },
  { value: 'applied', label: 'Aplicada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export const fiscalStatusLabel = (value) =>
  fiscalStatusOptions.find((option) => option.value === value)?.label || value || '';

export const fiscalStatusColor = (value) => {
  if (value === 'applied') return 'success';
  if (value === 'partially_applied') return 'warning';
  if (value === 'cancelled') return 'default';
  return 'info';
};

export const allocationStatusOptions = [
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'suggested', label: 'Sugerido' },
  { value: 'requires_review', label: 'Requiere revision' },
];

export const allocationStatusLabel = (value) =>
  allocationStatusOptions.find((option) => option.value === value)?.label || value || '';

export const allocationStatusColor = (value) => {
  if (value === 'confirmed') return 'success';
  if (value === 'suggested') return 'warning';
  return 'default';
};

export const commitmentLabel = (commitment) => {
  if (!commitment) return '';
  const reference = commitment.reference || `SO-${commitment.id}`;
  const date = formatDate(commitment.date);
  const missing = commitment.missing_total !== undefined ? ` | Faltante ${commitment.missing_total}` : '';
  return `${reference}${date ? ` | ${date}` : ''}${missing}`;
};

export const commitmentLineLabel = (line) => {
  if (!line) return '';
  const product = productLabel(line.expected_product) || line.comment || 'Producto pendiente';
  const missing = line.total_missing !== undefined ? ` | Faltante ${line.total_missing}` : '';
  return `Linea #${line.id} | ${product}${missing}`;
};

export const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('es-GT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const statusOptions = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'partial', label: 'Parcial' },
  { value: 'completed', label: 'Completada' },
  { value: 'with_missing', label: 'Con faltante' },
  { value: 'closed', label: 'Cerrada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export const commitmentStatusLabel = (status) =>
  statusOptions.find((option) => option.value === status)?.label || status || '';

export const commitmentStatusColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'partial' || status === 'with_missing') return 'warning';
  if (status === 'cancelled') return 'default';
  if (status === 'closed') return 'info';
  return 'default';
};

export const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (/[",\r\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
};

export const downloadCsv = (filename, headers, rows) => {
  const headerLine = headers.map((header) => csvEscape(header.label)).join(',');
  const body = (rows || []).map((row) =>
    headers.map((header) => {
      const value = typeof header.value === 'function' ? header.value(row) : row?.[header.key];
      return csvEscape(value);
    }).join(',')
  );
  const csv = [headerLine, ...body].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
